/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Button, Wrap, WrapItem} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CloseIcon} from '@salesforce/retail-react-app/app/components/icons'
import {REMOVE_FILTER} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const HIDDEN_SELECTED_REFINEMENTS = ['c_eswIsOrderableSku']

const SelectedRefinements = ({toggleFilter, selectedFilterValues, filters, handleReset, ...props}) => {
    const {formatMessage, formatNumber} = useIntl()
    const {currency} = useCurrency()

    let selectedFilters = []
    for (const key in selectedFilterValues) {
        const filters = selectedFilterValues[key].split('|')
        filters?.forEach((filter) => {
            let uiLabel = filter

            if (key === 'price') {
                // Parse price range string like (1..41)
                const match = filter.match(/\((\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\)/)
                if (match) {
                    const min = parseFloat(match[1])
                    const max = parseFloat(match[2])

                    // Format min and max as currency without decimals
                    const formattedMin = formatNumber(min, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })
                    const formattedMax = formatNumber(max, {
                        style: 'currency',
                        currency,
                        maximumFractionDigits: 0
                    })

                    uiLabel = `${formattedMin} - ${formattedMax}`
                }
            }

            const selected = {
                uiLabel,
                value: key,
                apiLabel: filter
            }

            if (
                selected.value !== 'htype' &&
                selected.value !== 'cgid' &&
                !HIDDEN_SELECTED_REFINEMENTS.includes(selected.value)
            ) {
                selectedFilters.push(selected)
            }
        })
    }

    return (
        <Wrap
            direction="row"
            align="center"
            display="flex"
            flexWrap="wrap"
            data-testid="sf-selected-refinements"
            spacing={props?.spacing || 1}
            mb={selectedFilters.length ? 4 : 0}
        >
            {selectedFilters?.map((filter, idx) => {
                return (
                    <WrapItem key={idx}>
                        <Box marginLeft={0}>
                            <Button
                                variant={'selectedFilter'}
                                iconSpacing={1}
                                rightIcon={
                                    <CloseIcon color="white" boxSize={4} mr="-7px" mb="-6px" />
                                }
                                onClick={() =>
                                    toggleFilter({value: filter.apiLabel}, filter.value, true)
                                }
                                aria-label={formatMessage(REMOVE_FILTER, {label: filter.uiLabel})}
                            >
                                {filter.uiLabel}
                            </Button>
                        </Box>
                    </WrapItem>
                )
            })}

            {selectedFilters?.length > 0 && (
                <WrapItem>
                    <Box>
                        <Button
                            padding={{sm: 0, base: 2}}
                            variant="link"
                            color={'grey.800'}
                            size="sm"
                            onClick={handleReset}
                            aria-label={formatMessage({
                                id: 'selected_refinements.action.assistive_msg.clear_all',
                                defaultMessage: 'Clear all filters'
                            })}
                        >
                            <FormattedMessage
                                defaultMessage="Clear All"
                                id="selected_refinements.action.clear_all"
                            />
                        </Button>
                    </Box>
                </WrapItem>
            )}
        </Wrap>
    )
}

SelectedRefinements.propTypes = {
    filters: PropTypes.array,
    selectedFilterValues: PropTypes.object,
    toggleFilter: PropTypes.func,
    handleReset: PropTypes.func
}

export default SelectedRefinements
